import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { fetchIbamaEntities, ibamaEntityParams } from '@/services/ibama';

const PAGE_SIZE = 50;

/**
 * The IBAMA company directory: every company an auto, a ledger case, an
 * instrument, a notification or a CTF registry names, BY NAME, never by
 * count. Companies only -- no natural person is loaded, so none can be found
 * here. The default list is the companies an auto or ledger case names; the
 * 730,000 CTF-only registrants join on request, because a due-diligence
 * search must find a target that was never fined and meet the absence as a
 * stated fact.
 */
export function IbamaEntitiesPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [includeRegistryOnly, setIncludeRegistryOnly] = useState(
    seed.get('include_registry_only') === 'true',
  );

  const filters = { query, include_registry_only: includeRegistryOnly || null };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ibama', 'entities', filters],
    queryFn: ({ pageParam }) =>
      fetchIbamaEntities({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:entities.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:entities.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:entities.explainer" />

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/ibama/export/entities?${ibamaEntityParams(filters).toString()}`}
        />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="ibama-entity-query" className="text-xs font-medium text-foreground">
            {t('ibama:entities.search.label')}
          </label>
          <input
            id="ibama-entity-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('ibama:entities.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={includeRegistryOnly}
            onChange={(event) => setIncludeRegistryOnly(event.target.checked)}
          />
          {t('ibama:entities.search.includeRegistryOnly')}
        </label>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('ibama:entities.search.submit')}
        </button>
      </form>

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-directory>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('ibama:entities.shape.title')}
          </h2>
          <Answer answer={data.directory} help={t('ibama:entities.shape.help')}>
            {(d) => (
              <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {(
                  [
                    ['entities', number.format(d.entities)],
                    ['withAutos', number.format(d.with_autos)],
                    ['withLedgerCases', number.format(d.with_ledger_cases)],
                    ['withTwoOrMore', number.format(d.with_two_or_more_cases)],
                    [
                      'registryOnly',
                      `${number.format(d.registry_only)} (${percent.format(d.registry_only_share)})`,
                    ],
                    ['ctfRegistered', number.format(d.ctf_registered)],
                    ['withInstruments', number.format(d.with_instruments)],
                    ['artefactNames', number.format(d.artefact_names)],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key} className="rounded-md border bg-muted/30 p-2" data-shape={key}>
                    <dt className="text-xs text-muted-foreground">
                      {t(`ibama:entities.shape.${key}`)}
                    </dt>
                    <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="ibama:entities.guide" />
          </div>
        </section>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ibama:entities.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="px-4 py-2 text-xs text-muted-foreground" data-total={data?.total}>
            {t('ibama:entities.count', {
              loaded: number.format(items.length),
              total: number.format(data?.total ?? 0),
            })}
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('ibama:entities.columns.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:entities.columns.cnpj')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:entities.columns.uf')}</th>
                  <th className="px-3 py-2 font-medium">{t('ibama:entities.columns.ctf')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:entities.columns.autos')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:entities.columns.cases')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:entities.columns.settled')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('ibama:entities.columns.instruments')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.cnpj}
                    className="border-t align-top"
                    data-cnpj={row.cnpj}
                    data-artefact={row.name_is_artefact || undefined}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/ibama/e/${row.cnpj}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {row.name}
                      </Link>
                      {row.name_is_artefact ? (
                        <div className="text-xs italic text-muted-foreground">
                          {t('ibama:entities.artefactName')}
                        </div>
                      ) : null}
                      {row.cnpj_valid === false ? (
                        <div className="text-xs italic text-muted-foreground">
                          {t('ibama:entities.cnpjInvalid')}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-muted-foreground">{row.cnpj}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.uf ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {row.ctf_registries.length
                        ? row.ctf_registries
                            .map((r) => t(`ibama:entities.registries.${r}`, { defaultValue: r }))
                            .join(', ')
                        : t('ibama:entities.registries.none')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {row.autos === 0 ? (
                        <span className="italic text-muted-foreground">
                          {t('ibama:entities.noAuto')}
                        </span>
                      ) : (
                        number.format(row.autos)
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.cases)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.settled)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.instruments)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('ibama:entities.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
